import React, { useState, useRef, useContext } from 'react';
import { RefObject } from 'react';
import { UserData } from '@/school-management/types/user-data';
import CsvDropzone, {
  CsvDropzoneRef,
} from '@/components/school-management/CsvDropzone/CsvDropzone';
import ClassUploadCsvModal from './ClassUploadCsvModal';
import ClassUsersTableModal from './ClassUsersTableModal';
import Popup from '@/components/school-management/Popup/Popup';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { studentService, teacherService } from '@/school-management/services';
import { withTrimmedPersonNamesBatch } from '@/utils/personName';
import { useNavigate, useParams } from 'react-router-dom';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import ClassCreatedModal from './ClassCreatedModal';
import { adminManagementService } from '@/admin/services/adminManagement';
import ExistingUsersModal from './ExistingUsersModal';
import AppModal from '@/components/AppModal/AppModal';
import TheButton from '@/components/school-management/TheButton/TheButton';
import classnames from 'classnames';
import classNames from './styles.module.scss';
import { EventDispatcherContext, DispatcherEvents } from '@/context/eventDispatcherContext';

export interface existingUsersInterface {
  totalUsers: number;
  addedUsers: number;
  existingUsers: number;
  existingUsersList: {
    firstName: string;
    lastName: string;
    email: string;
    error: string;
  }[];
}
interface ClassModalsManagerProps {
  showUploadModal: boolean;
  groupId: string;
  groupName?: string;
  setShowUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  addUsersToNewEntity: boolean;
  setAddUsersToNewEntity: React.Dispatch<React.SetStateAction<boolean>>;
  onUsersCreate: (users: UserData[]) => void;
  setShowUsersCreateError: React.Dispatch<React.SetStateAction<boolean>>;
  dealId?: string;
  isTeacher?: boolean;
  usersInClass?: number;
  onTeachersRefetch?: () => void;
  /** Called after a student-add success modal closes; caller decides navigation (e.g. to the class's own page or staying in place). */
  onStayPageStudentAddComplete?: (addedUsers: number) => void;
  refreshSchoolDetails?: () => void;
  placementTestRequired?: boolean;
}

const ClassModalsManager: React.FC<ClassModalsManagerProps> = ({
  showUploadModal,
  groupId,
  groupName,
  setShowUploadModal,
  addUsersToNewEntity,
  setAddUsersToNewEntity,
  onUsersCreate,
  setShowUsersCreateError,
  dealId,
  isTeacher,
  usersInClass = 0,
  onTeachersRefetch,
  onStayPageStudentAddComplete,
  refreshSchoolDetails,
  placementTestRequired,
}) => {
  const { user, userRole } = useAppSelector(authSelector);
  const navigate = useNavigate();
  const { schoolId } = useParams();
  const csvDropzoneRef = useRef<CsvDropzoneRef | null>(null);
  const [isCsvLoading, setIsCsvLoading] = useState(false);
  const [isCsvSending, setIsCsvSending] = useState(false);
  const [csvFilesAdded, setCsvFilesAdded] = useState<boolean>(false);
  const [usersData, setUsersData] = useState<UserData[]>([]);
  const [showNewUserInput, setShowNewUserInput] = useState(false);
  const [showUsersTableModal, setShowUsersTableModal] = useState(false);
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const { t } = useAdminTranslation();
  const [notifType, setNotifType] = useState<'success' | 'error' | 'info'>('success');
  const [showClassCreatedModal, setShowClassCreatedModal] = useState(false);
  const [showExistingUsersModal, setShowExistingUsersModal] =
    useState<existingUsersInterface | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');
  const [lastAddedUsersCount, setLastAddedUsersCount] = useState(0);
  const eventDispatcherContext = useContext(EventDispatcherContext);

  const onCloseUploadCsvModal = () => {
    setShowUploadModal(false);
    setCsvFilesAdded(false);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const onShowUsersTableModal = () => {
    scrollToTop();
    setShowUploadModal(false);
    setShowUsersTableModal(true);
  };

  const onBackUsersTableModal = () => {
    setCsvFilesAdded(false);
    setUsersData([]);
    setShowUsersTableModal(false);
    setShowUploadModal(true);
  };

  const onCloseClassCreatedModal = () => {
    setShowClassCreatedModal(false);

    if (isTeacher) {
      if (onTeachersRefetch) {
        setTimeout(() => {
          onTeachersRefetch();
        }, 500);
      }
      eventDispatcherContext.dispatch(DispatcherEvents.SCHOOL_DETAILS_CHANGE, null);
      navigate(`/schools/${schoolId}?tab=teachers`, {
        replace: true,
      });
      return;
    }

    onStayPageStudentAddComplete?.(lastAddedUsersCount);
  };

  const onCloseExistingUsersModal = () => {
    const existingUsersData = showExistingUsersModal;
    setShowExistingUsersModal(null);

    if (isTeacher) {
      if (onTeachersRefetch) {
        setTimeout(() => {
          onTeachersRefetch();
        }, 500);
      }
      eventDispatcherContext.dispatch(DispatcherEvents.SCHOOL_DETAILS_CHANGE, null);
      navigate(`/schools/${schoolId}?tab=teachers`, {
        replace: true,
      });
      return;
    }

    onStayPageStudentAddComplete?.(existingUsersData?.addedUsers ?? 0);
  };

  const onCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorModalMessage('');
    setShowUsersTableModal(false);
    setShowUploadModal(true);
  };

  const sanitizeString = (value: string) =>
    value
      .replace(/[\u200B-\u200D\uFEFF\u202A-\u202E\u2066-\u2069]/g, '')
      .replace(/\s+/g, '')
      .trim();

  const handleCsvUsersCreate = async () => {
    setIsCsvSending(true);

    try {
      const sanitizedUsersData = withTrimmedPersonNamesBatch(
        usersData.map(user => ({
          ...user,
          email: sanitizeString(user.email),
        }))
      );
      let response;

      const organizationId = user?.metadata?.organizationDetails?.organizationId || '';

      if (isTeacher) {
        response = await teacherService.createTeachers(
          organizationId,
          sanitizedUsersData,
          dealId || ''
        );
      } else {
        response = await studentService.createStudents(
          organizationId,
          groupId,
          sanitizedUsersData,
          dealId || ''
        );
      }

      // Wait 2 seconds for DynamoDB stream to update postgres
      await new Promise(resolve => setTimeout(resolve, 2000));

      const isSuccess = response?.data?.status === 'success';

      if (!isSuccess) {
        const { message, data } = response?.data ?? {};
        const licenseError = data?.licenseError;

        let errorMessage = t(
          isTeacher
            ? 'settings.add_teachers.teacher_creation_error'
            : 'settings.add_students.student_creation_error'
        );

        if (
          (message === 'not_enough_licenses' || data?.errorMessage === 'not_enough_licenses') &&
          licenseError
        ) {
          if (isTeacher) {
            errorMessage = t('settings.add_teachers.not_enough_licenses', {
              available: licenseError.available,
              requested: licenseError.requested,
            });
          } else {
            const keyPrefix =
              userRole === 'teacher'
                ? 'settings.add_students.not_enough_licenses_teacher'
                : 'settings.add_students.not_enough_licenses';
            const blocked = t(`${keyPrefix}_blocked` as any, {
              count: licenseError.requested,
              requested: licenseError.requested,
            });
            const remaining = t(`${keyPrefix}_remaining` as any, {
              count: licenseError.available,
              available: licenseError.available,
            });
            errorMessage = `${blocked} ${remaining}`;
          }
        }

        setUsersData([]);
        setCsvFilesAdded(false);
        setShowUsersTableModal(false);
        setErrorModalMessage(errorMessage.toString());
        setShowErrorModal(true);
        setShowUsersCreateError(true);
        return;
      }

      const addedUsers = response?.data?.data?.registeredUsers?.length || 0;
      const existingUsers = response?.data?.data?.unregisteredUsers?.length || 0;
      const totalUsers = usersData?.length || 0;

      setUsersData([]);
      setCsvFilesAdded(false);
      setShowUsersTableModal(false);
      setLastAddedUsersCount(addedUsers);

      if (existingUsers > 0) {
        setShowExistingUsersModal({
          addedUsers: addedUsers,
          existingUsers: existingUsers,
          totalUsers: totalUsers,
          existingUsersList: response
            ? response.data.data.unregisteredUsers.map((user: any) => {
                return {
                  firstName: user.firstName,
                  lastName: user.lastName,
                  email: user.email,
                  error: user.error,
                };
              })
            : [],
        });
      } else {
        setShowClassCreatedModal(true);
      }

      refreshSchoolDetails?.();
    } catch (err) {
      console.error('Error creating CSV users:', err);

      setUsersData([]);
      setCsvFilesAdded(false);
      setShowUsersTableModal(false);

      const errorMessage =
        err instanceof Error ? err.message : t('dynamicForm.notification.generalError');
      setErrorModalMessage(errorMessage.toString());
      setShowErrorModal(true);
      setShowUsersCreateError(true);
    } finally {
      setIsCsvSending(false);
    }
  };

  return (
    <>
      <ClassUploadCsvModal
        show={showUploadModal}
        addUsersToNewGroup={addUsersToNewEntity}
        csvFilesAdded={csvFilesAdded}
        isCsvLoading={isCsvLoading}
        usersData={usersData}
        setUsersData={setUsersData}
        setIsCsvLoading={setIsCsvLoading}
        setCsvFilesAdded={setCsvFilesAdded}
        csvDropzoneRef={csvDropzoneRef}
        onClose={onCloseUploadCsvModal}
        onAddManually={() => {
          if (isCsvLoading) {
            return;
          }
          setUsersData([]);
          setShowUploadModal(false);
          setShowNewUserInput(true);
          onShowUsersTableModal();
        }}
        onNext={() => {
          setShowNewUserInput(false);
          onShowUsersTableModal();
          setShowUploadModal(false);
        }}
        isTeacher={isTeacher}
        usersInClass={usersInClass}
      />

      <ClassUsersTableModal
        show={showUsersTableModal}
        addUsersToNewGroup={addUsersToNewEntity}
        usersData={usersData}
        setUsersData={setUsersData}
        disableSubmitButton={disableSubmitButton}
        setDisableSubmitButton={setDisableSubmitButton}
        showNewUserInput={showNewUserInput}
        setShowNewUserInput={setShowNewUserInput}
        isCsvSending={isCsvSending}
        onBack={onBackUsersTableModal}
        onSubmit={handleCsvUsersCreate}
        isTeacher={isTeacher}
        usersInClass={usersInClass}
      />

      <ClassCreatedModal
        show={showClassCreatedModal}
        addStudentsToNewGroup={addUsersToNewEntity}
        onClose={onCloseClassCreatedModal}
        isTeacher={isTeacher}
        groupName={groupName}
        placementTestRequired={placementTestRequired}
      />

      <ExistingUsersModal
        show={showExistingUsersModal ? true : false}
        onClose={onCloseExistingUsersModal}
        usersData={showExistingUsersModal}
        isTeacher={isTeacher}
      />

      {/* Error Modal */}
      {showErrorModal && (
        <AppModal show={showErrorModal} type='fade'>
          <div className={classnames(classNames.modal, classNames.copyModal)}>
            <h2>
              {isTeacher
                ? t('settings.add_teachers.teacher_creation_error')
                : t('settings.add_students.student_creation_error')}
            </h2>
            <span className={classNames.description}>{errorModalMessage}</span>
            <TheButton
              className={classNames.btn}
              text={t('done')}
              shouldRecolor={false}
              emitOnClick={onCloseErrorModal}
            />
          </div>
        </AppModal>
      )}
    </>
  );
};

export default ClassModalsManager;
